import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Notification } from "../common/Notification";
import { useForm } from "react-hook-form";
import { PATTERNS } from "../../util/patterns";
import { addFamilyMembersFPS } from "../../store/service";
import * as FamilyServiceActions from "../../store/familyService/actions";
import { ApplicationState } from "../../store";
import { getFlowByName } from "../../config/FlowName";
import * as NextStepFlowAction from "../../store/nextStepFlowService/actions";
import { useTranslation } from "react-i18next";

const FamilyProtectionDialog: React.FunctionComponent<{ closeDialog(): void }> = (props) => {
    const [isLoading, setisLoading] = useState(false);
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const dispatch = useDispatch();
    const addFamilyMemberFlow = useSelector((state: ApplicationState) => getFlowByName(state, "ADD-FAMILY-MEMBER"));
    const eZSChildSubscribers = useSelector((state: ApplicationState) =>
        state.familyServiceState && state.familyServiceState.eZSChildSubscribers ? state.familyServiceState.eZSChildSubscribers : []
    );
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const [familyAccount, setFamilyAccount] = useState({ firstName: "", lastName: "", email: "", isFPSExtented: true });
    const getFPSFamilySubscribers = () => dispatch(FamilyServiceActions.actionCreators.getFPSFamilySubscribers());
    const { register, errors, handleSubmit } = useForm({
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const closeDialog = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const refreshFamilyMembersInformation = () => {
        closeDialog();
        getFPSFamilySubscribers();
    };

    function onChangeHandler(event: any) {
        const name = event.currentTarget.name;
        setFamilyAccount({ ...familyAccount, [name as string]: event.currentTarget.value });
    }

    const saveItem = () => {
        const requestBody = {
            _addFamilyMembers: [familyAccount],
        };

        const firstName = familyAccount.firstName;
        const lastName = familyAccount.lastName;
        const email = familyAccount.email;

        if (eZSChildSubscribers.length <= 0) {
            setisLoading(true);
            addFamilyMembersFPS(requestBody)
                .then((data) => {
                    setisLoading(false);
                    if (data.isSuccess) {
                        if (addFamilyMemberFlow && addFamilyMemberFlow.flowStepStatus !== "COMPLETED") {
                            completeGetStartedItem(addFamilyMemberFlow.flowStepId, addFamilyMemberFlow.flowName ? addFamilyMemberFlow.flowName : " ");
                        }
                        Notification({
                            type: "success",
                            title: tLang("dashboard.addFamilyMemberDialog.title"),
                            message:
                                tLang("dashboard.addFamilyMemberDialog.successMsg1") +
                                firstName +
                                " " +
                                lastName +
                                tLang("dashboard.addFamilyMemberDialog.successMsg2") +
                                email +
                                " " +
                                tLang("dashboard.addFamilyMemberDialog.successMsg3"),
                        });
                        refreshFamilyMembersInformation();
                    } else {
                        Notification({
                            type: "error",
                            title: tLang("dashboard.addFamilyMemberDialog.titleError"),
                            message: tLang("dashboard.addFamilyMemberDialog.errorMsg"),
                        });
                        refreshFamilyMembersInformation();
                    }
                })
                .catch(function (error) {
                    setisLoading(false);
                    Notification({
                        type: "error",
                        title: "Add Family Member Error",
                        message: "Could not add family member. Please Try again later.",
                    });
                    refreshFamilyMembersInformation();
                });
        }
    };

    return (
        <React.Fragment>
            {!isLoading ? (
                <>
                    <div className="dialog-header">
                        <div className="dialog-icon">
                            <em className="las la-user-plus"></em>
                        </div>
                        <h1>{tLang("dashboard.addFamilyMemberDialog.title")}</h1>
                        <p>{tLang("dashboard.addFamilyMemberDialog.titleContent")}</p>
                    </div>

                    <form onSubmit={handleSubmit(saveItem)} id="add-fps-form-container">
                        <div className="dialog-body">
                            <div className={errors["firstName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-first-name">
                                            {tLang("dashboard.addFamilyMemberDialog.firstname")}{" "}
                                            <span className="required" title="Mandatory">
                                                *{" "}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            type="text"
                                            className={errors.firstName ? "status-error inputbox" : "inputbox"}
                                            id="edit-first-name"
                                            aria-labelledby="edit-first-name"
                                            name="firstName"
                                            defaultValue={familyAccount.firstName ? familyAccount.firstName : ""}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.FIRSTNAME.pattern,
                                            })}
                                            maxLength={30}
                                            aria-required="true"
                                        />
                                        {errors.firstName && errors.firstName.type === "pattern" && (
                                            <span className="form-error">{tLang(PATTERNS.FIRSTNAME.message)}</span>
                                        )}
                                        {errors.firstName && errors.firstName.type === "required" && <span className="form-error">{tLang("dashboard.addFamilyMemberDialog.ValidationError.firstname")}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["lastName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-last-name">
                                            {tLang("dashboard.addFamilyMemberDialog.lastname")}{" "}
                                            <span className="required" title="Mandatory">
                                                *{" "}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            type="text"
                                            className="inputbox"
                                            id="edit-last-name"
                                            aria-labelledby="edit-last-name"
                                            name="lastName"
                                            defaultValue={familyAccount.lastName ? familyAccount.lastName : ""}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.FIRSTNAME.pattern,
                                            })}
                                            maxLength={30}
                                            aria-required="true"
                                        />
                                        {errors.lastName && errors.lastName.type === "pattern" && <span className="form-error">{tLang(PATTERNS.FIRSTNAME.message)} </span>}

                                        {errors.lastName && errors.lastName.type === "required" && <span className="form-error">{tLang("dashboard.addFamilyMemberDialog.ValidationError.lastname")}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["email"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                        <label htmlFor="edit-email">
                                            {tLang("dashboard.addFamilyMemberDialog.email")}{" "}
                                            <span className="required" title="Mandatory">
                                                *{" "}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <input
                                            type="text"
                                            className="inputbox"
                                            id="edit-email"
                                            aria-labelledby="edit-email"
                                            name="email"
                                            defaultValue={familyAccount.email}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.EMAIL.pattern,
                                            })}
                                            maxLength={PATTERNS.EMAIL.maxLength}
                                            aria-required="true"
                                        />
                                        {errors.email && errors.email.type === "pattern" && <span className="form-error">{tLang(PATTERNS.EMAIL.message)}</span>}

                                        {errors.email && errors.email.type === "required" && <span className="form-error">{tLang("dashboard.addFamilyMemberDialog.ValidationError.email")}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <div className="row between-xs">
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="button" className="btn-default btn-link btn-no-padding btn-no-border" onClick={() => closeDialog()}>
                                        <span className="btn-text">{tLang("dashboard.addFamilyMemberDialog.cancelButton")}</span>
                                    </button>
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="submit" className="btn-default btn-primary">
                                        <span className="btn-text">{tLang("dashboard.addFamilyMemberDialog.saveButton")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </>
            ) : (
                <div className="box loading"></div>
            )}
        </React.Fragment>
    );
};

export default FamilyProtectionDialog;
